import { useEffect, useId, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";
import { Project } from "../../utils/interface/project";
import { uploadIfcModels, uploadThumbnail, validateIfcFile, validateReportFile } from "../../api/upload/file";
import { getFileNameFromDownloadResponse, extractApiError, isPresignedUrlExpired, isSafeUrl } from "../../utils/util";
import { updateProjectFiles, startIfcProcessing, getProjectIFCModelReport, getProjectById } from "../../api/Projects";
import { reportUpload } from "../../api/reports/reports";
import dummyProjectImage from "../../assets/Projects/project1.png";
import { useAuth } from "../../contexts/AuthContext";
import UserProfileCard from "../UserProfileCard";
const DUMMY_IMAGE_FALLBACK = dummyProjectImage;

interface ViewProjectProps {
  project: Project;
  onClose?: () => void;
  /** Called with the updated project after a successful IFC upload so the parent can update state and show new file names. */
  onProjectUpdated?: (project: Project) => void;
}

const TrackProgress = () => {
  const clipId = useId();
  return (
  <svg
    className="w-7 transition-transform duration-500 ease-in-out group-hover:rotate-180"
    id="Group_59"
    data-name="Group 59"
    xmlns="http://www.w3.org/2000/svg"
    width="27.164"
    height="27.154"
    viewBox="0 0 27.164 27.154"
  >
    <defs>
      <clipPath id={clipId}>
        <rect
          id="Rectangle_65"
          data-name="Rectangle 65"
          width="27.164"
          height="27.154"
          fill="#fff"
        />
      </clipPath>
    </defs>
    <g id="Group_58" data-name="Group 58" clipPath={`url(#${clipId})`}>
      <path
        id="Path_201"
        data-name="Path 201"
        d="M475.85,8.9l-5.837,1.9a7.459,7.459,0,0,0-6.423-4.662V0a13.589,13.589,0,0,1,12.26,8.9"
        transform="translate(-449.509)"
        fill="#fff"
      />
      <path
        id="Path_202"
        data-name="Path 202"
        d="M39.39,0V6.137A7.461,7.461,0,0,0,32.966,10.8L27.13,8.9A13.589,13.589,0,0,1,39.39,0"
        transform="translate(-26.306)"
        fill="#fff"
      />
      <path
        id="Path_203"
        data-name="Path 203"
        d="M613.3,328.051a13.558,13.558,0,0,1-5.2,10.689l-3.6-4.959a7.468,7.468,0,0,0,2.452-7.555l5.836-1.9a13.589,13.589,0,0,1,.516,3.721"
        transform="translate(-586.14 -314.479)"
        fill="#fff"
      />
      <path
        id="Path_204"
        data-name="Path 204"
        d="M8.8,333.771,5.2,338.73A13.6,13.6,0,0,1,.516,324.32l5.836,1.9A7.467,7.467,0,0,0,8.8,333.771"
        transform="translate(0 -314.469)"
        fill="#fff"
      />
      <path
        id="Path_205"
        data-name="Path 205"
        d="M212.911,659.749a13.589,13.589,0,0,1-15.152,0l3.6-4.958a7.461,7.461,0,0,0,7.946,0Z"
        transform="translate(-191.753 -634.902)"
        fill="#fff"
      />
    </g>
  </svg>
  );
};
const ViewProject = ({ project, onClose, onProjectUpdated }: ViewProjectProps) => {
  const navigate = useNavigate();
  const { userDetails } = useAuth();
  const isAdmin = userDetails?.type != null && String(userDetails.type) === "0";

  const fileInputRef = useRef<HTMLInputElement>(null);
  const reportFileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [reportFile, setReportFile] = useState<File | null>(null);
  const [reportUploading, setReportUploading] = useState(false);
  /** After a successful report upload, keep showing this name without refetching project. */
  const [uploadedReportFileName, setUploadedReportFileName] = useState<string | null>(null);
  /** IFC reports from GET /projects/ifc-reports/:id – items may be pending until ZIP is built. */
  type IfcReportItem = {
    ifcModelReportPreview?: string | null;
    key?: string;
    name?: string;
    isZip?: boolean;
    status?: "ready" | "pending" | "error" | string;
  };
  const [ifcReports, setIfcReports] = useState<IfcReportItem[]>([]);
  const [ifcReportsLoading, setIfcReportsLoading] = useState(false);
  const ifcReportsPollRef = useRef<number | null>(null);
  const thumbnailUrl =
    (project.projectThumbnailPreview ?? project.projectThumbnail) ||
    ((project as unknown as { projectThumbnailPreview?: string; projectThumbnail?: string })?.projectThumbnailPreview ?? (project as unknown as { projectThumbnail?: string })?.projectThumbnail) ||
    "";

  /** Get IFC display text from project.ifcModel (single key, array of keys, or legacy object/URL). */
  const getExistingIfcDisplay = (): string => {
    const raw = project.ifcModel ?? (project as { ifc_model?: unknown }).ifc_model;
    if (!raw) return "";
    if (Array.isArray(raw)) {
      const n = raw.length;
      if (n === 0) return "";
      const names = raw
        .map((k) => (typeof k === "string" ? k.split("/").filter(Boolean).pop() : null))
        .filter(Boolean) as string[];
      if (names.length > 0) return names.join(", ");
      return n === 1 ? "1 file" : `${n} IFC files`;
    }
    if (typeof raw === "object" && raw !== null && typeof (raw as { name?: string }).name === "string") {
      return (raw as { name: string }).name;
    }
    if (typeof raw === "string") {
      if (raw.startsWith("http")) {
        try {
          const segment = new URL(raw).pathname.split("/").filter(Boolean).pop();
          return segment ? decodeURIComponent(segment) : "";
        } catch {
          return "";
        }
      }
      return raw.split("/").filter(Boolean).pop() ?? "";
    }
    return "";
  };

  /** Get report file display name from project.report.reportUrl (filename from URL path or S3 key). */
  const getExistingReportFileName = (): string => {
    const reportUrl = project.report?.reportUrl;
    if (!reportUrl || typeof reportUrl !== "string") return "";
    try {
      const path = reportUrl.startsWith("http") ? new URL(reportUrl).pathname : reportUrl;
      const segment = path.split("/").filter(Boolean).pop();
      return segment ? decodeURIComponent(segment) : "";
    } catch {
      return "";
    }
  };

  const existingIfcDisplay = getExistingIfcDisplay();
  const existingReportFileName = getExistingReportFileName();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList?.length) {
      setSelectedFiles([]);
      return;
    }
    const files = Array.from(fileList);
    for (const file of files) {
      const error = await validateIfcFile(file);
      if (error) {
        toast.error(`${file.name}: ${error}`);
        setSelectedFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
    }
    setSelectedFiles(files);
  };

  const handleUploadIfc = async () => {
    if (!selectedFiles.length) return;
    if (!project?.id) {
      toast.error("Project not found. Cannot link IFC file.");
      return;
    }
    setUploading(true);
    try {
      const keys = await uploadIfcModels(selectedFiles, "ifcModels");
      const data = await updateProjectFiles(project.id, { ifcModel: keys });
      setUploadedCount(keys.length);
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (onProjectUpdated) {
        const fromApi = data?.project as { ifcModel?: string[]; ifc_model?: string[] } | undefined;
        const hasIfcModel = fromApi && (fromApi.ifcModel !== undefined || fromApi.ifc_model !== undefined);
        const updatedProject: Project = hasIfcModel
          ? { ...project, ...fromApi, ifcModel: fromApi.ifcModel ?? fromApi.ifc_model }
          : { ...project, ifcModel: keys };
        onProjectUpdated(updatedProject);
      }
      const processingResponse = await startIfcProcessing(project.id);
      const jobs = (processingResponse as { jobs?: Array<{ job_id?: string }> })?.jobs ?? [];
      const jobIds = jobs.map((j) => j.job_id).filter((id): id is string => Boolean(id));
      toast.success("IFC file(s) uploaded. Processing jobs queued.");
      navigate({
        to: "/upload-success/$projectId",
        params: { projectId: project.id },
        state: jobIds.length > 0 ? { jobIds } : undefined,
      });
    } catch (linkErr) {
      toast.error(extractApiError(linkErr));
    } finally {
      setUploading(false);
    }
  };

  const displayFileName =
    (selectedFiles.length > 0
      ? selectedFiles.length === 1
        ? selectedFiles[0].name
        : selectedFiles.map((f) => f.name).join(", ")
      : null) ||
    existingIfcDisplay ||
    (uploadedCount > 0 ? `${uploadedCount} file(s) uploaded` : null) ||
    "";
  const uploadButtonDisabled = !selectedFiles.length || uploading;

  /** Use ifcModelReportPreview for download (signed URL); fallback to csvFileUrl for backwards compatibility. */
  /** Use uploadControlPanelDocPreview (signed URL from API) for download; fallback to raw uploadControlPanelDoc for backwards compatibility. */
  const cpanelUrl =
    project.uploadControlPanelDocPreview ??
    (typeof project.uploadControlPanelDoc === "string"
      ? project.uploadControlPanelDoc
      : (project.uploadControlPanelDoc as { url?: string; downloadUrl?: string } | null)?.url ??
      (project.uploadControlPanelDoc as { url?: string; downloadUrl?: string } | null)?.downloadUrl ??
      null);
  const hasCpanelUrl = Boolean(cpanelUrl);
  /** Filename for control panel download: from stored path (e.g. "controlPanel/.../Name.xlsx") or fallback. */
  const cpanelFileName =
    (typeof project.uploadControlPanelDoc === "string" && project.uploadControlPanelDoc.includes("/")
      ? project.uploadControlPanelDoc.split("/").pop()
      : null) ?? "control-panel.xlsx";

  const triggerBrowserOpen = (url: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Opening file.");
  };

  const downloadFileByFetch = async (url: string, defaultFileName: string): Promise<"success" | "forbidden" | "failed"> => {
    try {
      const response = await fetch(url, { mode: "cors" });
      if (response.status === 403) return "forbidden";
      if (!response.ok) return "failed";
      const fileName = getFileNameFromDownloadResponse(response, url, defaultFileName);
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
      return "success";
    } catch {
      return "failed";
    }
  };

  const handleDownloadByUrl = async (
    url: string,
    defaultFileName: string,
    getFreshUrl?: () => Promise<string | null>,
  ) => {
    if (!isSafeUrl(url)) return;

    let activeUrl = url;
    if (isPresignedUrlExpired(activeUrl) && getFreshUrl) {
      toast("Download link expired. Fetching a fresh link...");
      const fresh = await getFreshUrl();
      if (fresh && isSafeUrl(fresh)) activeUrl = fresh;
      else {
        toast.error("Download link expired. Please refresh and try again.");
        return;
      }
    }

    const firstTry = await downloadFileByFetch(activeUrl, defaultFileName);
    if (firstTry === "success") return;

    if (firstTry === "forbidden" && getFreshUrl) {
      toast("Download link expired. Retrying with a fresh link...");
      const fresh = await getFreshUrl();
      if (fresh && isSafeUrl(fresh)) {
        const retry = await downloadFileByFetch(fresh, defaultFileName);
        if (retry === "success") return;
      }
      toast.error("Download link expired. Please refresh and try again.");
      return;
    }

    if (firstTry === "failed") {
      triggerBrowserOpen(activeUrl);
    }
  };

  const handleDownloadCpanel = () => {
    if (!cpanelUrl) return;
    handleDownloadByUrl(cpanelUrl, cpanelFileName, async () => {
      if (!project?.id) return null;
      const latest = await getProjectById(project.id);
      const fresh =
        latest?.uploadControlPanelDocPreview ??
        (typeof latest?.uploadControlPanelDoc === "string"
          ? latest.uploadControlPanelDoc
          : (latest?.uploadControlPanelDoc as { url?: string; downloadUrl?: string } | null)?.url ??
          (latest?.uploadControlPanelDoc as { url?: string; downloadUrl?: string } | null)?.downloadUrl ??
          null);
      return fresh ?? null;
    });
  };

  /** Derive original filename from S3 key (last path segment, URL-decoded). */
  const getOriginalFileName = (key: string | undefined, fallback: string): string => {
    if (!key || typeof key !== "string") return fallback;
    const segment = key.split("/").filter(Boolean).pop();
    if (!segment) return fallback;
    try {
      return decodeURIComponent(segment);
    } catch {
      return segment || fallback;
    }
  };

  /** Download all IFC reports sequentially using ifcModelReportPreview, with original filenames from key (Option C). */
  const handleDownloadIfcReports = async () => {
    const list = ifcReports.filter((r) => r.ifcModelReportPreview);
    if (list.length === 0) return;
    const delayMs = 400;
    for (let i = 0; i < list.length; i++) {
      const url = list[i].ifcModelReportPreview ?? "";
      const fileName = getOriginalFileName(list[i].key, list[i].name || `report-${i + 1}.csv`);
      const reportKey = list[i].key;
      await handleDownloadByUrl(url, fileName, async () => {
        if (!project?.id || !reportKey) return null;
        const latestReports = await getProjectIFCModelReport(project.id);
        const latest = Array.isArray(latestReports)
          ? latestReports.find((r) => (r as { key?: string }).key === reportKey)
          : null;
        return (latest as { ifcModelReportPreview?: string } | null)?.ifcModelReportPreview ?? null;
      });
      if (i < list.length - 1) await new Promise((r) => setTimeout(r, delayMs));
    }
  };

  const handleReportFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const err = validateReportFile(file);
      if (err) {
        toast.error(err);
        e.target.value = "";
        setReportFile(null);
        return;
      }
    }
    setReportFile(file ?? null);
  };

  const handleUploadReport = async () => {
    if (!reportFile || !project?.id) return;
    setReportUploading(true);
    const fileName = reportFile.name;
    try {
      const reportUrl = await uploadThumbnail(reportFile, "reports");
      await reportUpload({
        projectId: project.id,
        reportUrl,
        status: "report-uploaded",
      });
      toast.success("Report uploaded.");
      setUploadedReportFileName(fileName);
      setReportFile(null);
      if (reportFileInputRef.current) reportFileInputRef.current.value = "";
    } catch (error) {
      toast.error(extractApiError(error));
    } finally {
      setReportUploading(false);
    }
  };

  /** IFC reports: from project.ifcModelReportPreview when array (multi-IFC), else fetch legacy endpoint. */
  useEffect(() => {
    if (!isAdmin || !project?.id) return;
    const preview = project.ifcModelReportPreview;
    if (Array.isArray(preview) && preview.length > 0) {
      setIfcReports(preview as IfcReportItem[]);
      return;
    }
    let cancelled = false;

    const clearPoll = () => {
      if (ifcReportsPollRef.current != null) {
        window.clearInterval(ifcReportsPollRef.current);
        ifcReportsPollRef.current = null;
      }
    };

    const fetchOnce = async (): Promise<IfcReportItem[]> => {
      const data: unknown = await getProjectIFCModelReport(project.id);
      const list = Array.isArray(data) ? data : (data as { reports?: unknown[] })?.reports ?? [];
      return (Array.isArray(list) ? list : []) as IfcReportItem[];
    };

    const startPollingIfNeeded = (items: IfcReportItem[]) => {
      const hasReadyUrl = items.some((r) => Boolean(r.ifcModelReportPreview));
      const hasPending = items.some(
        (r) =>
          (r.isZip === true && (r.status === "pending" || !r.ifcModelReportPreview)) ||
          r.status === "pending"
      );

      // If backend says pending/no URLs yet, keep polling until ready.
      if (!hasReadyUrl && hasPending && ifcReportsPollRef.current == null) {
        ifcReportsPollRef.current = window.setInterval(async () => {
          try {
            const next = await fetchOnce();
            if (cancelled) return;
            setIfcReports(next);
            const nowReady = next.some((r) => Boolean(r.ifcModelReportPreview));
            const stillPending = next.some((r) => r.status === "pending" || (r.isZip === true && !r.ifcModelReportPreview));
            if (nowReady || !stillPending) {
              clearPoll();
              setIfcReportsLoading(false);
            }
          } catch {
            // Keep polling; transient errors happen with presign/cache.
          }
        }, 3000);
      }
    };

    setIfcReportsLoading(true);
    fetchOnce()
      .then((items) => {
        if (cancelled) return;
        setIfcReports(items);
        startPollingIfNeeded(items);
      })
      .catch(() => {
        if (!cancelled) setIfcReports([]);
      })
      .finally(() => {
        if (!cancelled && ifcReportsPollRef.current == null) setIfcReportsLoading(false);
      });

    return () => {
      cancelled = true;
      clearPoll();
    };
  }, [isAdmin, project?.id, project?.ifcModelReportPreview]);

  const UploadArrowIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="30"
      viewBox="0 0 24.135 30.348"
      className="overflow-visible"
    >
      {/* File body */}
      <path
        d="M.015,15.128V4.615C.022,1.689,1.679.021,4.585.016,8.2.012,11.815.038,15.427,0
         A4.713,4.713,0,0,1,18.95,1.389c1.2,1.17,2.441,2.293,3.68,3.42a4.411,4.411,0,0,1,1.5,3.41
         c0,5.924.014,11.849,0,17.772a4.1,4.1,0,0,1-3.837,4.346,3.92,3.92,0,0,1-.531,0q-7.669.021-15.34,0
         a4.131,4.131,0,0,1-4.4-4.471Z"
        fill="currentColor"
        className="text-white group-hover:text-[#E761A0] transition-colors duration-300 "
      />

      {/* Arrow (move UP + fade on hover) */}
      <g className="transition-transform duration-300 ease-out group-hover:-translate-y-2 group-hover:opacity-0">
        <path
          d="M13.346,11.168l.255-.126a10.14,10.14,0,0,0,.756.9,1.243,1.243,0,0,0,1.755.119
           c.028-.024.054-.049.079-.075a1.257,1.257,0,0,0-.034-1.777l-.017-.016
           q-1.533-1.6-3.119-3.143a1.209,1.209,0,0,0-1.886,0Q9.576,8.569,8.062,10.132
           a1.266,1.266,0,1,0,1.777,1.785c.289-.269.563-.555.983-.97v1.479
           c0,1.011-.019,2.022.026,3.031a1.175,1.175,0,0,0,1.161,1.189h.007
           a1.217,1.217,0,0,0,1.3-1.129v-.009a6.707,6.707,0,0,0,.029-.885V11.169"
          fill="currentColor"
          className="text-[#E761A0] transition-colors duration-300"
        />
      </g>

      {/* Bottom line */}
      <path
        d="M7.4,21.217h9.3"
        stroke="currentColor"
        strokeWidth="2"
        className="text-[#E761A0] group-hover:text-white transition-colors duration-300"
      />
    </svg>
  );
  const DownloadIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="30"
      viewBox="0 0 24 30"
      className="text-white theme-purple"
    >
      {/* File body → white → pink */}
      <path
        d="M3 2h12l6 6v18a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"
        fill="currentColor"
        className="transition-colors duration-300 group-hover:text-[#65449B]"
      />

      {/* Fold */}
      <path
        d="M15 2v6h6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="transition-colors duration-300 group-hover:text-[#65449B] "
      />

      {/* Arrow group */}
      <g
        className="
        text-[#65449B]
        transition-all duration-300 ease-out
        group-hover:translate-y-4
        group-hover:opacity-0
        group-hover:text-white
      "
      >
        <line
          x1="12"
          y1="11"
          x2="12"
          y2="17"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />

        <polyline
          points="9,14 12,17 15,14"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>

      {/* Bottom line */}
      <line
        x1="7"
        y1="22"
        x2="17"
        y2="22"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        className="text-[#65449B] group-hover:text-white transition-colors duration-300"
      />
    </svg>
  );
  return (
    <div className="min-h-full  relative overflow-hidden">
      {/* Wavy background accents */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <svg
          className="absolute bottom-0 left-0 w-full opacity-20"
          viewBox="0 0 1200 200"
          preserveAspectRatio="none"
        >
          <path
            fill="none"
            stroke="#E761A0"
            strokeWidth="1"
            opacity="0.4"
            d="M0,100 Q300,50 600,100 T1200,100 L1200,200 L0,200 Z"
          />
          <path
            fill="none"
            stroke="#E761A0"
            strokeWidth="1"
            opacity="0.25"
            d="M0,140 Q400,80 800,140 T1600,140"
          />
        </svg>
        <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-[#E761A0]/5 to-transparent" />
      </div>

      <div className="relative z-10 ">
        <UserProfileCard />
        {/* Back to Projects */}
        {onClose ? (
          <Link
            to="/project-management"
            className="inline-flex items-center gap-2 text-[#E761A0] hover:underline font-medium mb-3 text-lg"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Projects
          </Link>
        ) : (
          <Link
            to="/project-management"
            className="inline-flex items-center gap-2 text-[#E761A0] hover:underline font-medium 2xl:mb-[35px] mb-5 text-lg"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Projects
          </Link>
        )}

        {/* Main card */}
        <div className="rounded-2xl border  border-[#868686]  shadow-xl overflow-hidden">
          {/* Project info row: title + description | image */}
          <div className="p-7 2xl:p-[32px_52px_36px_53px] flex flex-col lg:flex-row gap-8 lg:gap-10">
            <div className="flex-1 min-w-0 flex flex-col">
              <h1 className="text-2xl md:text-3xl lg:text-[44px] font-semibold text-[#E761A0] mb-6">
                {project.name}
              </h1>
              <div className="flex flex-col lg:flex-row lg:items-start gap-6 lg:gap-8 min-w-0">
                <div className="min-w-0 flex-1 lg:max-w-[451px]">
                  <p className="text-white/90 text-base lg:text-lg leading-relaxed whitespace-pre-wrap break-words">
                    {project.summary?.trim() || "No description."}
                  </p>
                  {project.location && (
                    <p className="mt-3 text-white text-base lg:text-lg">
                      Location: {project.location}
                    </p>
                  )}
                  {project.companyName && (
                    <p className="text-white text-base lg:text-lg">
                      Company: {project.companyName}
                    </p>
                  )}
                </div>
                <div className="flex-shrink-0 w-full sm:w-[360px] h-[300px] 2xl:w-[553px] 2xl:h-[329px] lg:flex-shrink-0">
                  <img
                    src={(thumbnailUrl || DUMMY_IMAGE_FALLBACK) || undefined}
                    alt={project.name}
                    className="w-full h-full object-cover rounded-xl border border-white/10"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = DUMMY_IMAGE_FALLBACK;
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="" />

          {/* Actions */}
          {userDetails?.type === "2" ? (
            <div className="">
              <h2 className="text-[20px] 2xl:text-[22px] font-semibold text-white p-7 md:p-8 pt-0 md:pt-[0px] md:pb-[32px] 2xl:pl-[53px]">
                Actions
              </h2>
              <div className="border-t border-[#707070] w-full"></div>
              <div className="flex xl:flex-nowrap flex-wrap items-center 2xl:gap-x-[37px] 2xl:gap-y-5 gap-4  p-7  pt-[46px] md:pb-[53px] 2xl:pl-[53px]">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".ifc"
                  multiple
                  className="hidden"
                  onChange={handleFileChange}
                />
                <div className="flex w-full max-w-[463px] h-[53px] rounded-full border border-[#868686] overflow-hidden">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="browse-file-btn shrink-0 lg:px-9 px-3 custom-file h-full bg-white text-gray-800 text-base rounded-full font-semibold uppercase tracking-wide hover:bg-gray-100 transition-colors"
                  >
                    BROWSE FILE
                  </button>
                  <input
                    type="text"
                    readOnly
                    value={displayFileName}
                    placeholder="No file chosen"
                    className="flex-1 min-w-0 h-full px-4 bg-transparent text-gray-400 text-sm placeholder-gray-500 focus:outline-none border-0"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleUploadIfc}
                  disabled={uploadButtonDisabled}
                  className="inline-flex min-w-[260px] items-center gap-2 px-5 2xl:py-[12px] py-[9px] rounded-[27px] bg-[#E761A0] text-white font-semibold text-base uppercase tracking-wide hover:bg-[#d9558f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed justify-center sm:w-auto w-full "
                >
                  {uploading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <UploadArrowIcon />
                      Upload IFC model
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    project?.id
                      ? navigate({
                          to: "/ifc-processing/$id",
                          params: { id: project.id },
                        })
                      : undefined
                  }
                  className="inline-flex ml-auto items-center gap-2 px-5 2xl:py-[12px] py-[9px] rounded-[27px] bg-[#E761A0] text-white font-semibold text-base uppercase tracking-wide hover:bg-[#d9558f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[237px] justify-center sm:w-auto w-full "
                >
                  <TrackProgress />
                  Track progress
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="p-7 md:p-8 pt-0 md:pt-[0px] md:pb-[35px] 2xl:pl-[53px]">
                <div className="flex flex-wrap items-center gap-4 sm:gap-6 2xl:gap-[53px] actions-buttons">
                  <h2 className="text-[20px] 2xl:text-[22px] font-semibold text-white ">
                    Actions
                  </h2>

                  <button
                    type="button"
                    className="group flex items-center justify-center gap-1.5 sm:gap-2 rounded-[27px] border-[2px] border-[#65449B] bg-[#65449B] text-white px-4 sm:px-6 py-2 2xl:py-[11px] text-sm sm:text-[16px] font-semibold tracking-wide transition-all duration-300 ease-out hover:bg-transparent hover:shadow-none hover:border-[#65449B]   sm:w-auto sm:flex-1 sm:max-w-[148px] shrink-0 xl:min-h-[54px] min-h-[45px] text-[12px] xl:text-[16px]  w-full"
                    onClick={() =>
                      navigate({
                        to: "/project-management/edit/$id",
                        params: { id: project?.id ?? "" },
                      })
                    }
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="26.997"
                      height="26.953"
                      viewBox="0 0 26.997 26.953"
                      className="text-white group-hover:text-purple-400 transition-all duration-300 ease-out"
                      fill="currentColor"
                    >
                      <path
                        id="Path_235"
                        data-name="Path 235"
                        d="M22.392,16.076a1.4,1.4,0,0,1,2.8,0v7.2a3.685,3.685,0,0,1-3.674,3.674H3.674A3.685,3.685,0,0,1,0,23.279V5.318A3.685,3.685,0,0,1,3.674,1.644h7.678a1.4,1.4,0,0,1,0,2.8H3.674a.877.877,0,0,0-.875.875V23.279a.877.877,0,0,0,.875.875H21.516a.877.877,0,0,0,.875-.875ZM23.206.332l3.46,3.46a1.141,1.141,0,0,1,0,1.608L15.27,16.8l-5.854.827.765-5.874L21.6.332a1.141,1.141,0,0,1,1.608,0"
                      />
                    </svg>
                    <span className="uppercase tracking-wide">EDIT</span>
                  </button>
                  {isAdmin && (
                    <button
                      type="button"
                      disabled={ifcReportsLoading || !ifcReports.some((r) => r.ifcModelReportPreview)}
                      onClick={handleDownloadIfcReports}
                      className="group  sm:max-w-fit max-w-full flex items-center justify-center gap-1.5 sm:gap-[20px] rounded-[27px] border-[2px] border-[#65449B] bg-[#65449B] text-white px-4 sm:px-6 py-2 2xl:py-[9px] text-sm sm:text-[16px] font-semibold tracking-wide transition-all duration-300 ease-out hover:bg-transparent hover:shadow-none hover:border-[#65449B]   sm:w-auto sm:flex-1  shrink-0 xl:min-h-[54px] min-h-[45px] text-[12px] xl:text-[16px]  w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {ifcReportsLoading ? (
                        <>
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin shrink-0" />
                          <span className="uppercase tracking-wide">
                            Loading CSV's
                          </span>
                        </>
                      ) : (
                        <>
                          <DownloadIcon />
                          <span className="uppercase tracking-wide">
                            Download CSV's
                          </span>
                        </>
                      )}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={!hasCpanelUrl}
                    onClick={handleDownloadCpanel}
                    className="group min-w-[260px] flex items-center justify-center gap-1.5 sm:gap-[20px] rounded-[27px] border-[2px] border-[#65449B] bg-[#65449B] text-white px-4 sm:px-6 py-2 2xl:py-[9px] text-sm sm:text-[16px] font-semibold tracking-wide transition-all duration-300 ease-out hover:bg-transparent hover:shadow-none hover:border-[#65449B] w-full sm:w-auto sm:flex-1 sm:max-w-[148px] shrink-0 xl:min-h-[54px] min-h-[45px] text-[12px] xl:text-[16px] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <DownloadIcon />
                    <span className="uppercase tracking-wide">
                      DOWNLOAD C PANEL
                    </span>
                  </button>
                </div>
              </div>
              <div className="border-t border-[#707070] w-full" aria-hidden />
              <div className="p-7 md:p-8 pt-[30px] md:pt-[30px] md:pb-[33px] 2xl:pl-[53px]">
                <div className="flex flex-wrap items-center gap-4 sm:gap-6 2xl:gap-[53px]">
                  <h2 className="text-[20px] 2xl:text-[22px] font-semibold text-white ">
                    Uploads
                  </h2>
                  <input
                    ref={reportFileInputRef}
                    type="file"
                    accept="*/*"
                    className="hidden"
                    onChange={handleReportFileChange}
                  />
                  <div className="flex w-full max-w-[463px] h-[53px] rounded-full border border-[#868686] overflow-hidden">
                    <button
                      type="button"
                      onClick={() => reportFileInputRef.current?.click()}
                      className="browse-file-btn shrink-0 px-9 h-full bg-white text-gray-800 text-sm lg:text-base rounded-full font-semibold uppercase tracking-wide hover:bg-gray-100 transition-colors"
                    >
                      BROWSE FILE
                    </button>
                    <input
                      type="text"
                      readOnly
                      value={reportFile?.name ?? uploadedReportFileName ?? existingReportFileName ?? ""}
                      placeholder="No file chosen"
                      className="flex-1 min-w-0 h-full px-4 bg-transparent text-gray-400 text-sm placeholder-gray-500 focus:outline-none border-0"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleUploadReport}
                    disabled={!reportFile || reportUploading}
                    className="theme-btn min-w-[230px] sm:w-auto w-full max-w-full  gap-[20px] group overflow-hidden flex items-center px-5 2xl:py-[6px] py-2 rounded-[27px] border-2 border-[#E761A0] hover:text-[#E761A0] text-white transition-all duration-300 ease-out hover:bg-transparent btn-hover-color sm:max-w-[226px] uppercase font-weight-600 xl:min-h-[54px] min-h-[45px] text-[12px] xl:text-[16px] disabled:opacity-50 disabled:cursor-not-allowed justify-center"
                  >
                    {reportUploading ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <UploadArrowIcon />
                        Upload Report
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewProject;